import { cn } from '@dfh-corp-sites/ui/utils'
import React, { FC } from 'react'

import {
  SanityOrMagentoProductImage,
  SanityProductImageSelection,
} from '../../../../lib/products/images'
import { isNotNull } from '../../../../utils/collectionTools'
import { imageUrlFor } from '../../../../utils/image-url'
import ItemImagePreview from './ItemImagePreview'
import { ItemImagesProps } from './ItemImages'

export interface ItemImagePreviewSourceFactory {
  (
    image: SanityOrMagentoProductImage,
  ): Pick<JSX.IntrinsicElements['img'], 'alt' | 'src' | 'srcSet'> & { key: React.Key }
}

export interface ItemImagePreviewsProps {
  className?: string
  images: ReadonlyArray<SanityOrMagentoProductImage>
  imagePreviewSourceFactory: ItemImagePreviewSourceFactory | undefined
  selectedImage: SanityOrMagentoProductImage | null | undefined
  setSelectedImage: (image: SanityOrMagentoProductImage | null | undefined) => void
  showItemImagePreviews: boolean
  size?: ItemImagesProps['size']
}

function isSanityProductImage(
  image: SanityOrMagentoProductImage,
): image is SanityProductImageSelection {
  return 'asset' in image && image.asset !== undefined
}

/**
 * Default image preview source factory for product details page
 */
const previewSourceForImage: ItemImagePreviewSourceFactory = (image) => {
  if (!isSanityProductImage(image)) {
    return { alt: image.label ?? '', key: image.url, src: image.url, srcSet: '' }
  }

  const partial = imageUrlFor(image.asset).width(45).auto('format')
  const src1x = partial.url()
  const src2x = partial.dpr(2).url()
  const src3x = partial.dpr(3).url()
  const srcSet = [src1x, src2x ? `${src2x} 2x` : null, src3x ? `${src3x} 3x` : null]
    .filter(isNotNull)
    .join(', ')

  return { alt: image.alt ?? '', key: image.asset._id, src: src1x ?? '', srcSet }
}

const ItemImagePreviews: FC<ItemImagePreviewsProps> = ({
  className,
  images,
  imagePreviewSourceFactory = previewSourceForImage,
  selectedImage,
  setSelectedImage,
  showItemImagePreviews,
  size,
}) => {
  if (!showItemImagePreviews) {
    return null
  }

  return (
    <fieldset aria-label="Image previews" className={cn('flex md:justify-end gap-2.5', className)}>
      <legend className="sr-only">Image previews</legend>
      {images.map((itemImage) => {
        const { alt = '', key, src = '', srcSet } = imagePreviewSourceFactory(itemImage)
        return (
          <ItemImagePreview
            className="item-image-preview"
            key={key}
            alt={alt}
            src={src}
            srcSet={srcSet}
            size={size}
            selected={itemImage === selectedImage}
            onImageSelected={() => setSelectedImage(itemImage)}
          />
        )
      })}
    </fieldset>
  )
}

export default ItemImagePreviews
